<template>
  <div v-if="messageStore.aiResponses.length > 0" class="space-y-2">
    <div
      v-for="aiResponse in messageStore.aiResponses"
      :key="aiResponse.timestamp"
      class="border border-neutral-300 rounded-md overflow-hidden bg-white"
    >
      <!-- Accordion Header -->
      <button
        type="button"
        class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 cursor-pointer"
        :aria-expanded="isExpanded(aiResponse.timestamp) ? 'true' : 'false'"
        :aria-controls="`ai-response-panel-${aiResponse.timestamp}`"
        @click="toggle(aiResponse)"
      >
        <div class="flex items-center gap-3 min-w-0">
          <span
            class="font-medium text-sm text-neutral-700 truncate flex items-center gap-1"
          >
            <span>{{ formatTime(aiResponse.timestamp) }}</span>
            <span
              v-if="aiResponse.originalMessage"
              class="text-neutral-400 max-w-[220px] truncate"
            >
              • {{ shorten(aiResponse.originalMessage) }}
            </span>
          </span>
          <span
            v-if="aiResponse.isNew"
            class="text-[10px] font-semibold uppercase tracking-wide bg-blue-600 text-white px-2 py-0.5 rounded-full"
          >
            New
          </span>
        </div>
        <svg
          :class="[
            'w-4 h-4 text-neutral-500 transition-transform duration-200',
            isExpanded(aiResponse.timestamp) ? 'rotate-180' : 'rotate-0',
          ]"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <!-- Accordion Content -->
      <div
        v-show="isExpanded(aiResponse.timestamp)"
        :id="`ai-response-panel-${aiResponse.timestamp}`"
        class="px-4 pb-4 pt-2 border-t border-neutral-200 bg-neutral-50"
        :aria-hidden="isExpanded(aiResponse.timestamp) ? 'false' : 'true'"
      >
        <AiResponse :latest-ai-response="aiResponse" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useMessageStore } from '@/stores/messages';
import AiResponse from './AiResponse.vue';

const messageStore = useMessageStore();

// Track expanded accordion items (allow multiple open)
const expandedIds = ref<string[]>([]);

// Utility to format timestamp
const formatTime = (ts: string) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Truncate original message for header display
const shorten = (text?: string, max = 60) => {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
};

// Toggle accordion entry
const isExpanded = (ts: string) => expandedIds.value.includes(ts);

const toggle = (aiResponse: { timestamp: string; isNew?: boolean }) => {
  const ts = aiResponse.timestamp;
  if (isExpanded(ts)) {
    // Collapse only this one
    expandedIds.value = expandedIds.value.filter((id) => id !== ts);
  } else {
    // Expand this one (keep others open)
    expandedIds.value = [...expandedIds.value, ts];
    if (aiResponse.isNew) {
      messageStore.markAiResponseOpened(ts);
    }
  }
};
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.18s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
